import React, { useState, useEffect } from "react";
import "../Styles/Loan.css";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "react-toastify";
import logo from "../assets/rcslogo.png";
import {
  Landmark,
  Wallet,
  BadgeDollarSign,
  TrendingUp,
  Search,
  Plus,
  ChevronRight,
  CreditCard,
  CheckCircle2,
  LoaderCircle,
  X,
  Printer,
  BookOpen,
} from "lucide-react";

const Loan = () => {
  const navigate = useNavigate();

  /* ========================================
     STATE
  ======================================== */

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [loans, setLoans] = useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [selectedLoan, setSelectedLoan] =
    useState(null);

  const [
    isTransactionModalOpen,
    setIsTransactionModalOpen,
  ] = useState(false);

  /* ========================================
     NEW LOAN FORM
  ======================================== */

  const [clientName, setClientName] =
    useState("");

  const [
    clientPhoneNumber,
    setClientPhoneNumber,
  ] = useState("");

  const [loanAmount, setLoanAmount] =
    useState("");

  const [interestRate, setInterestRate] =
    useState("");

  const [loanTerm, setLoanTerm] =
    useState("");

  const [startDate, setStartDate] =
    useState("");

  const [loanType, setLoanType] =
    useState("Straight Line");

  /* ========================================
     PAYMENT FORM
  ======================================== */

  const [amount, setAmount] = useState("");

  const [paymentDate, setPaymentDate] =
    useState("");


const [
  isPaymentFlowOpen,
  setIsPaymentFlowOpen,
] = useState(false);

const [
  paymentStep,
  setPaymentStep,
] = useState("search");

const [
  paymentSearch,
  setPaymentSearch,
] = useState("");

const [
  paymentClient,
  setPaymentClient,
] = useState(null);

const [
  paymentLoadingMessage,
  setPaymentLoadingMessage,
] = useState(
  "Processing payment..."
);


  /* ========================================
     FETCH LOANS
  ======================================== */

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const data = await api.getLoans();

        setLoans(data || []);
      } catch (error) {
        console.error(error);

        toast.error(
          "Failed to load loans."
        );
      }
    };

    fetchLoans();
  }, []);

  /* ========================================
     FILTERED CLIENTS
  ======================================== */

  const filteredLoans = loans.filter(
    (loan) => {
      const term =
        searchTerm.toLowerCase();

      return (
        loan.client_name
          ?.toLowerCase()
          .includes(term) ||
        String(loan.loan_id).includes(
          term
        ) ||
        loan.client_phone
          ?.toLowerCase()
          .includes(term)
      );
    }
  );

  /* ========================================
     STATS
  ======================================== */

  const totalLoanCount = loans.length;

  const totalOutstanding = loans.reduce(
    (sum, loan) =>
      sum +
      Number(
        loan.remaining_balance || 0
      ),
    0
  );

  const totalPrincipal = loans.reduce(
    (sum, loan) =>
      sum +
      Number(loan.loan_amount || 0),
    0
  );

  const totalInterest = loans.reduce(
    (sum, loan) =>
      sum +
      Number(
        loan.total_interest || 0
      ),
    0
  );

  /* ========================================
     MODALS
  ======================================== */

  const handleNewLoan = () =>
    setIsModalOpen(true);

  const handleCloseModal = () =>
    setIsModalOpen(false);

  /* ========================================
     CALCULATIONS
  ======================================== */

  const calculateStraightLineLoanDetails =
    () => {
      const amount =
        Number(loanAmount);

      const rate =
        Number(interestRate) / 100;

      const term = Number(loanTerm);

      if (!amount || !rate || !term) {
        return {
          totalInterest: 0,
          monthlyPayment: 0,
          totalPayable: 0,
        };
      }

      const totalInterest =
        amount * rate;

      const totalPayable =
        amount + totalInterest;

      const monthlyPayment =
        totalPayable / term;

      return {
        totalInterest,
        monthlyPayment,
        totalPayable,
      };
    };

  const calculateReducingBalanceLoanDetails =
    () => {
      let totalInterest = 0;

      const amount =
        Number(loanAmount);

      const rate =
        Number(interestRate) / 100;

      const term = Number(loanTerm);

      const monthlyRate = rate / 12;

      let remainingPrincipal = amount;

      const monthlyPayment =
        (amount * monthlyRate) /
        (1 -
          Math.pow(
            1 + monthlyRate,
            -term
          ));

      for (let i = 0; i < term; i++) {
        const interestPayment =
          remainingPrincipal *
          monthlyRate;

        const principalRepayment =
          monthlyPayment -
          interestPayment;

        remainingPrincipal -=
          principalRepayment;

        totalInterest +=
          interestPayment;
      }

      const totalPayable =
        amount + totalInterest;

      return {
        totalInterest,
        monthlyPayment,
        totalPayable,
      };
    };

  /* ========================================
     CREATE LOAN
  ======================================== */

  const handleSubmit = async () => {
    if (
      !clientName ||
      !loanAmount ||
      !interestRate ||
      !loanTerm ||
      !startDate ||
      !clientPhoneNumber
    ) {
      toast.warn(
        "Complete all fields."
      );

      return;
    }

    let loanDetails;

    if (
      loanType === "Straight Line"
    ) {
      loanDetails =
        calculateStraightLineLoanDetails();
    } else {
      loanDetails =
        calculateReducingBalanceLoanDetails();
    }

    const {
      totalInterest,
      monthlyPayment,
      totalPayable,
    } = loanDetails;

    const endDate = new Date(
      startDate
    );

    endDate.setMonth(
      endDate.getMonth() +
        Number(loanTerm)
    );

    const newLoan = {
      client_name: clientName,

      client_phone:
        clientPhoneNumber,

      loan_type: loanType,

      loan_amount:
        Number(loanAmount),

      interest_rate:
        Number(interestRate),

      loan_term:
        Number(loanTerm),

      monthly_payment:
        monthlyPayment,

      total_interest:
        totalInterest,

      total_payable:
        totalPayable,

      remaining_balance:
        totalPayable,

      status: "Active",

      start_date: startDate,

      end_date: endDate
        .toISOString()
        .split("T")[0],
    };

    try {
      const createdLoan =
        await api.createLoan(
          newLoan
        );

      if (createdLoan) {
        setLoans((prev) => [
          ...prev,
          createdLoan,
        ]);

        toast.success(
          "Loan created successfully."
        );

        setIsModalOpen(false);

        setClientName("");
        setClientPhoneNumber("");
        setLoanAmount("");
        setInterestRate("");
        setLoanTerm("");
        setStartDate("");
      }
    } catch (error) {
      console.error(error);

      toast.error(
        "Failed to create loan."
      );
    }
  };

  /* ========================================
     DELETE LOAN
  ======================================== */

  const handleDeleteLoan = async (
    loan_id
  ) => {
    const confirmed =
      window.confirm(
        "Delete this loan?"
      );

    if (!confirmed) return;

    try {
      const success =
        await api.deleteLoan(
          loan_id
        );

      if (success) {
        setLoans((prev) =>
          prev.filter(
            (loan) =>
              loan.loan_id !==
              loan_id
          )
        );

        toast.success(
          "Loan deleted."
        );
      }
    } catch (error) {
      console.error(error);

      toast.error(
        "Delete failed."
      );
    }
  };

  /* ========================================
     ADD PAYMENT
  ======================================== */

const handleAddTransaction =
  async () => {

    if (
      !amount ||
      !paymentDate ||
      !paymentClient
    ) {
      toast.warn(
        "Complete payment details."
      );

      return;
    }

    try {

      setPaymentStep("loading");

      const loadingMessages = [
        "Processing payment...",
        "Updating balances...",
        "Generating confirmation...",
        "Preparing receipt...",
        "Finalizing transaction...",
      ];

      let current = 0;

      const interval =
        setInterval(() => {

          current++;

          if (
            current <
            loadingMessages.length
          ) {
            setPaymentLoadingMessage(
              loadingMessages[current]
            );
          }

        }, 2000);

      // Fake cinematic delay 😭
      await new Promise(
        (resolve) =>
          setTimeout(
            resolve,
            10000
          )
      );

      clearInterval(interval);

      const paymentAmount =
        Number(amount);

      await api.addLoanPayment(
        paymentClient.loan_id,
        {
          loan_id:
            paymentClient.loan_id,

          payment_amount:
            paymentAmount,

          payment_date:
            paymentDate,
        }
      );

      setLoans((prev) =>
        prev.map((loan) =>
          loan.loan_id ===
          paymentClient.loan_id
            ? {
                ...loan,

                remaining_balance:
                  Math.max(
                    0,
                    loan.remaining_balance -
                      paymentAmount
                  ),
              }
            : loan
        )
      );

      setPaymentStep(
        "success"
      );

    } catch (error) {

      console.error(error);

      toast.error(
        "Payment failed."
      );

      setPaymentStep("entry");
    }
  };



const handlePrintReceipt = () => {

  if (!paymentClient) return;

  const paymentAmount =
    Number(amount);

  const balanceBefore =
    paymentClient.remaining_balance;

  const finalBalance =
    Math.max(
      balanceBefore -
        paymentAmount,
      0
    );

  const receiptWindow =
    window.open(
      "",
      "_blank",
      "width=400,height=700"
    );

  receiptWindow.document.write(`
    <html>
      <head>
        <title>
          Loan Receipt
        </title>

        <style>

          body {
            font-family:
              Arial,
              sans-serif;

            padding: 30px;

            color: #052b49;
          }

          .receipt {
            max-width: 350px;

            margin: auto;
          }

          .center {
            text-align: center;
          }

          .business {
            font-size: 14px;

            line-height: 1.7;

            margin-bottom: 25px;
          }

          h2 {
            margin-top: 30px;

            margin-bottom: 30px;

            font-size: 24px;

            text-align: center;
          }

          .row {
            margin-bottom: 18px;
          }

          .label {
            font-size: 13px;

            color: #64748b;
          }

          .value {
            font-size: 18px;

            font-weight: bold;

            margin-top: 5px;
          }

          .divider {
            border-top:
              1px dashed #cbd5e1;

            margin:
              25px 0;
          }

          .footer {
            margin-top: 40px;

            text-align: center;

            font-size: 13px;

            line-height: 1.8;

            color: #475569;
          }

        </style>

      </head>

      <body>

        <div class="receipt">

          <div class="center business">

            <strong>
              RCS TOPUP AND BILLING ENTITY
            </strong>

            <br />

            22 BELLE PLAINE WAKENAAM

            <br />

            662-7987 |
            www.rcsgy.com

          </div>

          <h2>
            Loan Installment Receipt
          </h2>

          <div class="row">

            <div class="label">
              Loan ID
            </div>

            <div class="value">
              #${paymentClient.loan_id}
            </div>

          </div>

          <div class="row">

            <div class="label">
              Client Name
            </div>

            <div class="value">
              ${paymentClient.client_name}
            </div>

          </div>

          <div class="divider"></div>

          <div class="row">

            <div class="label">
              Loan Balance Before Payment
            </div>

            <div class="value">
              $${balanceBefore.toLocaleString()}
            </div>

          </div>

          <div class="row">

            <div class="label">
              Payment Amount
            </div>

            <div class="value">
              $${paymentAmount.toLocaleString()}
            </div>

          </div>

          <div class="row">

            <div class="label">
              Final Balance
            </div>

            <div class="value">
              $${finalBalance.toLocaleString()}
            </div>

          </div>

          <div class="divider"></div>

          <div class="footer">

            THANK YOU for letting us
            help you achieve your
            goals with RCS Finance.

          </div>

        </div>

        <script>
          window.onload = () => {
            window.print();
          }
        </script>

      </body>
    </html>
  `);

  receiptWindow.document.close();
};


  /* ========================================
     JSX
  ======================================== */

  return (
    <div className="loan-modern-page">

      {/* HERO */}

      <div className="loan-modern-hero">

        <div className="loan-modern-hero-left">

          <div className="loan-modern-tag">
            <Landmark size={14} />
            RCS FINANCE
          </div>

          <h1>
            Loan Portfolio
          </h1>

          <p>
            Monitor client loans,
            repayments, balances
            and financial
            performance.
          </p>

<div className="loan-modern-hero-actions">

  <button
    className="loan-modern-new-btn"
    onClick={handleNewLoan}
  >
    <Plus size={18} />
    New Loan
  </button>

  <button
    className="loan-modern-secondary-btn"
    onClick={() => {
      setIsPaymentFlowOpen(true);
      setPaymentStep("search");
      setPaymentClient(null);
      setAmount("");
      setPaymentSearch("");
    }}
  >
    <CreditCard size={18} />
    Receive Payment
  </button>

  <button
    className="loan-modern-secondary-btn"
    onClick={() =>
      navigate("/appendix")
    }
  >
    <Landmark size={18} />
    Appendix
  </button>

</div>

        </div>

      </div>

      {/* STATS */}

      <div className="loan-modern-stats">

        <div className="loan-modern-stat-card teal">

          <div className="loan-modern-stat-icon">
            <Wallet size={24} />
          </div>

          <span>
            Total Loans
          </span>

          <h2>
            {totalLoanCount}
          </h2>

        </div>

        <div className="loan-modern-stat-card blue">

          <div className="loan-modern-stat-icon">
            <BadgeDollarSign size={24} />
          </div>

          <span>
            Outstanding
          </span>

          <h2>
            $
            {totalOutstanding.toLocaleString()}
          </h2>

        </div>

        <div className="loan-modern-stat-card gold">

          <div className="loan-modern-stat-icon">
            <TrendingUp size={24} />
          </div>

          <span>
            Principal
          </span>

          <h2>
            $
            {totalPrincipal.toLocaleString()}
          </h2>

        </div>

        <div className="loan-modern-stat-card red">

          <div className="loan-modern-stat-icon">
            <Landmark size={24} />
          </div>

          <span>
            Interest
          </span>

          <h2>
            $
            {totalInterest.toLocaleString()}
          </h2>

        </div>

      </div>

      {/* SEARCH */}

      <div className="loan-modern-toolbar">

        <div className="loan-modern-search-wrapper">

          <Search size={20} />

          <input
            type="text"
            placeholder="Search clients, phone number or loan ID..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

        </div>

      </div>

      {/* LOOKUP EXPERIENCE */}

      <div className="loan-client-lookup-section">

        {!searchTerm ? (

          <div className="loan-client-empty-state">

            <div className="loan-client-empty-icon">
              <Search size={42} />
            </div>

            <h2>
              Start typing to
              search clients
            </h2>

            <p>
              Search clients by
              name, phone number,
              loan ID or account
              details.
            </p>

          </div>

        ) : (

          <div className="loan-client-results">

            {filteredLoans.length >
            0 ? (

              filteredLoans.map(
                (loan) => (

                  <div
                    key={
                      loan.loan_id
                    }
                    className="loan-client-result-card"
                    onClick={() =>
                      navigate(
                        `/loan/${loan.loan_id}`
                      )
                    }
                  >

                    <div className="loan-client-result-left">

                      <div className="loan-client-avatar">
                        {loan.client_name?.charAt(
                          0
                        )}
                      </div>

                      <div>

                        <h3>
                          {
                            loan.client_name
                          }
                        </h3>

                        <span>
                          Loan #
                          {
                            loan.loan_id
                          }
                        </span>

                      </div>

                    </div>

                    <div className="loan-client-result-center">

                      <div>

                        <label>
                          Outstanding
                        </label>

                        <strong>
                          $
                          {loan.remaining_balance?.toLocaleString()}
                        </strong>

                      </div>

                      <div>

                        <label>
                          Monthly
                        </label>

                        <strong>
                          $
                          {loan.monthly_payment?.toLocaleString()}
                        </strong>

                      </div>

                    </div>

                    <div className="loan-client-result-right">

                      <div
                        className={`loan-modern-status ${loan.status?.toLowerCase()}`}
                      >
                        {
                          loan.status
                        }
                      </div>

                      <ChevronRight
                        size={20}
                      />

                    </div>

                  </div>

                )
              )

            ) : (

              <div className="loan-client-no-results">

                <h3>
                  No matching
                  clients found
                </h3>

                <p>
                  Try another
                  client name,
                  phone number or
                  loan ID.
                </p>

              </div>

            )}

          </div>

        )}

      </div>

      {/* PAYMENT MODAL */}

      {isTransactionModalOpen &&
        selectedLoan && (

          <div className="loan-modern-modal-overlay">

            <div className="loan-modern-modal">

              <h2>
                Add Payment
              </h2>

              <p>
                {
                  selectedLoan.client_name
                }
              </p>

              <label>
                Amount
              </label>

              <input
                type="number"
                value={amount}
                onChange={(e) =>
                  setAmount(
                    e.target.value
                  )
                }
              />

              <label>
                Payment Date
              </label>

              <input
                type="date"
                value={paymentDate}
                onChange={(e) =>
                  setPaymentDate(
                    e.target.value
                  )
                }
              />

              <div className="loan-modern-modal-actions">

                <button
                  className="submit"
                  onClick={
                    handleAddTransaction
                  }
                >
                  Submit
                </button>

                <button
                  className="cancel"
                  onClick={() =>
                    setIsTransactionModalOpen(
                      false
                    )
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        )}


{/* PAYMENT EXPERIENCE */}

{isPaymentFlowOpen && (

  <div className="loan-paymentflow-overlay">

    <div className="loan-paymentflow-modal">

      {/* SEARCH STEP */}

      {paymentStep === "search" && (

        <>

          <div className="loan-paymentflow-header">

            <h2>
              Search Client
            </h2>

            <button
              onClick={() =>
                setIsPaymentFlowOpen(false)
              }
            >
              <X size={18} />
            </button>

          </div>

          <div className="loan-paymentflow-search">

            <Search size={20} />

            <input
              type="text"
              placeholder="Search client name or loan ID..."
              value={paymentSearch}
              onChange={(e) =>
                setPaymentSearch(
                  e.target.value
                )
              }
            />

          </div>

<div className="loan-paymentflow-results">

  {!paymentSearch.trim() ? (

    <div className="loan-paymentflow-empty">

      <div className="loan-paymentflow-emptyicon">

        <Search size={44} />

      </div>

      <h3>
        Start typing to search clients
      </h3>

      <p>
        Search by client name or
        loan account number.
      </p>

    </div>

  ) : (

loans
  .filter((loan) => {

    const term =
      paymentSearch
        .trim()
        .toLowerCase();

    const remainingBalance =
      Number(loan.remaining_balance || 0);

    const status =
      String(loan.status || "")
        .trim()
        .toLowerCase();

    // Do not allow payments on loans
    // with no outstanding balance
    if (remainingBalance <= 0) {
      return false;
    }

    // Do not show completed loans
    if (status === "completed") {
      return false;
    }

    return (
      loan.client_name
        ?.toLowerCase()
        .includes(term) ||

      String(loan.loan_id)
        .includes(term)
    );
  })

      .map((loan) => (

        <div
          key={loan.loan_id}
          className="loan-paymentflow-client"
          onClick={() => {

            setPaymentClient(
              loan
            );

            setPaymentStep(
              "entry"
            );
          }}
        >

          <div>

            <h3>
              {loan.client_name}
            </h3>

            <span>
              Loan #
              {loan.loan_id}
            </span>

          </div>

          <strong>
            $
            {loan.remaining_balance?.toLocaleString()}
          </strong>

        </div>

      ))

  )}

</div>

        </>

      )}

      {/* ENTRY STEP */}

      {paymentStep === "entry" &&
        paymentClient && (

          <>

            <div className="loan-paymentflow-header">

              <h2>
                Receive Payment
              </h2>

              <button
                onClick={() =>
                  setIsPaymentFlowOpen(false)
                }
              >
                <X size={18} />
              </button>

            </div>

            <div className="loan-paymentflow-clientcard">

              <h3>
                {
                  paymentClient.client_name
                }
              </h3>

              <span>
                Remaining Balance
              </span>

              <strong>
                $
                {paymentClient.remaining_balance?.toLocaleString()}
              </strong>

            </div>

            <label>
              Payment Amount
            </label>

            <input
              type="number"
              value={amount}
              onChange={(e) =>
                setAmount(
                  e.target.value
                )
              }
            />

            <label>
              Payment Date
            </label>

            <input
              type="date"
              value={paymentDate}
              onChange={(e) =>
                setPaymentDate(
                  e.target.value
                )
              }
            />

            <button
              className="loan-paymentflow-submit"
              onClick={
                handleAddTransaction
              }
            >
              Confirm Payment
            </button>

          </>

      )}

      {/* LOADING */}

      {paymentStep === "loading" && (

<div className="loan-paymentflow-loading">

  <div className="loan-payment-loader-container">

    {/* GLOW */}

    <div className="loan-payment-loader-glow" />

    {/* SPINNER */}

    <div className="loan-payment-loader-spinner">

      {/* PRIMARY RING */}

      <div className="loan-payment-loader-ring" />

      {/* SECONDARY RING */}

      <div className="loan-payment-loader-ring-secondary" />

      {/* LOGO */}

      <div className="loan-payment-loader-logo">

        <img
          src={logo}
          alt="RCS Logo"
        />

      </div>

    </div>

    {/* TEXT */}

    <div className="loan-payment-loader-text">

      <h2>
        Processing Payment
      </h2>

      <p>
        {paymentLoadingMessage}
      </p>

    </div>

  </div>

</div>

      )}

      {/* SUCCESS */}

      {paymentStep === "success" && (

        <div className="loan-paymentflow-success">

          <div className="loan-paymentflow-successicon">

            <CheckCircle2 size={70} />

          </div>

          <h2>
            Payment Confirmed
          </h2>

          <p>
            Transaction completed
            successfully.
          </p>

          <div className="loan-paymentflow-successactions">

<button
  onClick={
    handlePrintReceipt
  }
>

  <Printer size={18} />

  Print Receipt

</button>

            <button
              className="close"
              onClick={() => {

                setIsPaymentFlowOpen(
                  false
                );

                setPaymentStep(
                  "search"
                );
              }}
            >
              Close
            </button>

          </div>

        </div>

      )}

    </div>

  </div>

)}


      {/* CREATE LOAN MODAL */}

      {isModalOpen && (

        <div className="loan-modern-modal-overlay">

          <div className="loan-modern-modal large">

            <h2>
              Create Loan
            </h2>

            <div className="loan-modern-form-grid">

              <div>

                <label>
                  Client Name
                </label>

                <input
                  type="text"
                  value={clientName}
                  onChange={(e) =>
                    setClientName(
                      e.target.value
                    )
                  }
                />

              </div>

              <div>

                <label>
                  Phone Number
                </label>

                <input
                  type="text"
                  value={
                    clientPhoneNumber
                  }
                  onChange={(e) =>
                    setClientPhoneNumber(
                      e.target.value
                    )
                  }
                />

              </div>

              <div>

                <label>
                  Loan Type
                </label>

                <select
                  value={loanType}
                  onChange={(e) =>
                    setLoanType(
                      e.target.value
                    )
                  }
                >
                  <option>
                    Straight Line
                  </option>

                  <option>
                    Reducing Balance
                  </option>
                </select>

              </div>

              <div>

                <label>
                  Loan Amount
                </label>

                <input
                  type="number"
                  value={
                    loanAmount
                  }
                  onChange={(e) =>
                    setLoanAmount(
                      e.target.value
                    )
                  }
                />

              </div>

              <div>

                <label>
                  Interest %
                </label>

                <input
                  type="number"
                  value={
                    interestRate
                  }
                  onChange={(e) =>
                    setInterestRate(
                      e.target.value
                    )
                  }
                />

              </div>

              <div>

                <label>
                  Loan Term
                </label>

                <input
                  type="number"
                  value={loanTerm}
                  onChange={(e) =>
                    setLoanTerm(
                      e.target.value
                    )
                  }
                />

              </div>

              <div>

                <label>
                  Start Date
                </label>

                <input
                  type="date"
                  value={startDate}
                  onChange={(e) =>
                    setStartDate(
                      e.target.value
                    )
                  }
                />

              </div>

            </div>

            <div className="loan-modern-modal-actions">

              <button
                className="submit"
                onClick={
                  handleSubmit
                }
              >
                Create Loan
              </button>

              <button
                className="cancel"
                onClick={
                  handleCloseModal
                }
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default Loan;